import React, { useCallback, useMemo } from "react";
import { MainLayout } from "../layout/mainLayout";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { VehiclePositionInfo } from "../../models/vehicle/vehiclePosition";
import { MonitorViewMap } from "./monitorViewMap";
import { MonitorViewSidebar } from "./sidebar/monitorViewSidebar";
import { Base } from "../../framework/base";
import { useAppSelector } from "../../framework/customStore";
import { hasActiveWorkTime } from "./monitorViewUtils";

import {
    EmployeeData,
    MonitorViewMainLayoutProps,
    VehicleData,
} from "../../models/monitorView/monitorViewTypes";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { WorkShiftDto } from "../../models/workShift/workShift";

export const MonitorViewMainLayout = ({
    positions,
    loading,
    workShifts,
    transportOrders,
    reloadKey,
    onRefresh,
}: MonitorViewMainLayoutProps) => {
    const employees = useAppSelector((state) => state.employees.employees);

    const {
        primaryFilter,
        selectedVehicleGroupIds,
        workTimeTypeIds: selectedWorkTimeTypeIds,
        selectedTelematicsFilters,
        showWorkingWithoutPhase,
        noWork,
        sortOrder,
    } = useAppSelector((state) => state.monitorViewFilters);
    const vehicles = useAppSelector((state) => state.monitorView.vehicles);

    const { isVehicleRouteView, mapData } = useAppSelector(
        (state) => state.monitorView
    );

    // Vehicle filter: apply primary filter + sub-filters
    const vehiclePrimaryFilter = useCallback(
        (p: VehiclePositionInfo) => {
            if (primaryFilter === "all") return true;

            const isWorking = hasActiveWorkTime(p);

            if (primaryFilter === "working") {
                if (!isWorking) return false;
                // Apply work time type sub-filters
                const workTimeTypeId =
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId;
                if (workTimeTypeId === null || workTimeTypeId === undefined) {
                    // Working without a phase
                    return showWorkingWithoutPhase;
                }
                return (
                    !selectedWorkTimeTypeIds ||
                    selectedWorkTimeTypeIds.includes(workTimeTypeId)
                );
            }

            if (primaryFilter === "notWorking") {
                if (isWorking) return false;
                // Apply telematics sub-filters
                if (selectedTelematicsFilters.length === 0) return true;
                return selectedTelematicsFilters.includes(p.driving);
            }

            return true;
        },
        [
            primaryFilter,
            selectedWorkTimeTypeIds,
            selectedTelematicsFilters,
            showWorkingWithoutPhase,
        ]
    );

    // Employee filter: apply primary filter + sub-filters
    const employeeFilter = useCallback(
        (p: VehiclePositionInfo) => {
            if (primaryFilter === "all") {
                // Legacy behavior: show based on noWork and workTimeType filters
                if (
                    !p?.employeeWorkTimeTypes ||
                    p?.employeeWorkTimeTypes.length === 0
                ) {
                    return noWork;
                }
                const hasNoWorkTime =
                    p.employeeWorkTimeTypes[0].workTimeTypeId === null;
                const matchesType =
                    !selectedWorkTimeTypeIds ||
                    p.employeeWorkTimeTypes.some((w) =>
                        selectedWorkTimeTypeIds?.includes(w.workTimeTypeId)
                    );
                return noWork ? hasNoWorkTime || matchesType : matchesType;
            }

            if (primaryFilter === "working") {
                if (
                    !p?.employeeWorkTimeTypes ||
                    p?.employeeWorkTimeTypes.length === 0
                ) {
                    return false;
                }
                const workTimeTypeId =
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId;
                if (workTimeTypeId === null || workTimeTypeId === undefined) {
                    return showWorkingWithoutPhase;
                }
                return (
                    !selectedWorkTimeTypeIds ||
                    selectedWorkTimeTypeIds.includes(workTimeTypeId)
                );
            }

            if (primaryFilter === "notWorking") {
                if (
                    !p?.employeeWorkTimeTypes ||
                    p?.employeeWorkTimeTypes.length === 0
                ) {
                    return true; // No work time = not working
                }
                return (
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId === null ||
                    p.employeeWorkTimeTypes[0]?.workTimeTypeId === undefined
                );
            }

            return true;
        },
        [
            primaryFilter,
            noWork,
            selectedWorkTimeTypeIds,
            showWorkingWithoutPhase,
        ]
    );

    const mapCommonData = (position: VehiclePositionInfo) => ({
        brand: position?.brand,
        carId: position?.carId,
        codriverGmt: position?.codriverGmt,
        codriverId: position?.codriverId,
        coords: { lat: position?.latitude, lng: position?.longitude },
        driverGmt: position?.driverGmt,
        driverId: position?.driverId,
        driving: position?.driving,
        drivingGmt: position?.drivingGmt,
        employeeWorkTimeTypes: position?.employeeWorkTimeTypes,
        latitude: position?.latitude,
        longitude: position?.longitude,
        registerNumber: position?.registerNumber,
        speed: position?.speed,
        timestamp: position?.timestamp,
        vehicleId: position?.vehicleId,
        workTimeTypeName:
            position?.employeeWorkTimeTypes?.[0]?.workTimeTypeName,
        workTimeTypeType:
            position?.employeeWorkTimeTypes?.[0]?.workTimeTypeType,
    });

    const mapVehicleData = (p: VehiclePositionInfo): VehicleData => ({
        ...mapCommonData(p),
        employeeName: p.employeeWorkTimeTypes?.[0]?.employeeName,
        employeeId: p.employeeWorkTimeTypes?.[0]?.employeeId,
    });

    const mapEmployeeData = (
        e: EmployeeListItemDto,
        positions: VehiclePositionInfo[],
        workShifts: WorkShiftDto[]
    ): EmployeeData => {
        const eid = e.id?.toLowerCase();
        const position = positions?.find((p) =>
            p.employeeWorkTimeTypes.some(
                (w) => w.employeeId?.toLowerCase() === eid
            )
        );
        return {
            ...mapCommonData(position),
            employeeId: e.id,
            employeeName: e?.fullName,
            salaryGroupId: e?.salaryGroupId,
            workShifts: workShifts.filter(
                (w) => w.employeeId?.toLowerCase() === eid
            ),
            currentWorkShift: workShifts
                .filter(
                    (w) => w.employeeId?.toLowerCase() === eid && !w.endTime
                )
                .sort(
                    (a, b) =>
                        new Date(b.startTime).getTime() -
                        new Date(a.startTime).getTime()
                )?.[0],
        };
    };

    const vehicleData: VehicleData[] = useMemo(() => {
        const mapped =
            positions
                ?.filter(
                    (p) =>
                        selectedVehicleGroupIds.length === 0 ||
                        selectedVehicleGroupIds.includes(
                            vehicles?.find((v) => v.id === p.vehicleId)
                                ?.vehicleGroupId
                        )
                )
                .map(mapVehicleData)
                .sort((a, b) => {
                    if (sortOrder === "employeeName") {
                        return Base.stringCompare(
                            a.employeeName ?? "",
                            b.employeeName ?? ""
                        );
                    }
                    return Base.stringCompare(
                        a.registerNumber,
                        b.registerNumber
                    );
                }) ?? [];

        const mappedVehicleIds = new Set(
            mapped.map((v) => v.vehicleId?.toLowerCase())
        );

        // Add vehicles that have work shift time slots but no GPS position
        for (const shift of workShifts ?? []) {
            for (const slot of shift.workShiftTimeSlots ?? []) {
                if (
                    !slot.vehicleId ||
                    mappedVehicleIds.has(slot.vehicleId.toLowerCase())
                )
                    continue;

                const vehicleInfo = vehicles?.find(
                    (v) => v.id?.toLowerCase() === slot.vehicleId?.toLowerCase()
                );
                if (
                    !vehicleInfo ||
                    (selectedVehicleGroupIds.length > 0 &&
                        !selectedVehicleGroupIds.includes(
                            vehicleInfo.vehicleGroupId
                        ))
                )
                    continue;

                const employee = employees?.find(
                    (e) =>
                        e.id?.toLowerCase() === shift.employeeId?.toLowerCase()
                );

                mapped.push({
                    vehicleId: slot.vehicleId,
                    registerNumber: vehicleInfo.registerNumber ?? null,
                    brand: vehicleInfo.brand ?? null,
                    employeeId: shift.employeeId,
                    employeeName: employee?.fullName ?? null,
                    workTimeTypeName: slot.workTimeTypeName,
                    workTimeTypeType: slot.workTimeTypeType,
                    employeeWorkTimeTypes: [
                        {
                            employeeId: shift.employeeId,
                            employeeName: employee?.fullName ?? "",
                            workTimeTypeId: slot.workTimeTypeId,
                            workTimeTypeName: slot.workTimeTypeName,
                            workTimeTypeType: slot.workTimeTypeType,
                        },
                    ],
                    // No GPS data available
                    carId: null,
                    codriverGmt: null,
                    codriverId: null,
                    coords: { lat: 0, lng: 0 },
                    driverGmt: null,
                    driverId: null,
                    driving: undefined,
                    drivingGmt: null,
                    latitude: 0,
                    longitude: 0,
                    speed: 0,
                    timestamp: null,
                } as VehicleData);

                mappedVehicleIds.add(slot.vehicleId.toLowerCase());
            }
        }

        // Attach transport orders to vehicles
        if (transportOrders?.length) {
            const now = new Date();
            const ordersByVehicle = new Map<string, typeof transportOrders>();
            for (const order of transportOrders) {
                if (!order.vehicleId) continue;
                const start = order.scheduledStartDateTime
                    ? new Date(order.scheduledStartDateTime)
                    : null;
                const end = order.scheduledEndDateTime
                    ? new Date(order.scheduledEndDateTime)
                    : null;
                if (start && start > now) continue;
                if (end && end < now) continue;
                const vid = order.vehicleId.toLowerCase();
                if (!ordersByVehicle.has(vid)) {
                    ordersByVehicle.set(vid, []);
                }
                ordersByVehicle.get(vid).push(order);
            }
            for (const vehicle of mapped) {
                const vid = vehicle.vehicleId?.toLowerCase();
                if (vid && ordersByVehicle.has(vid)) {
                    vehicle.orders = ordersByVehicle.get(vid);
                }
            }
        }

        return mapped;
    }, [
        positions,
        selectedVehicleGroupIds,
        sortOrder,
        workShifts,
        employees,
        vehicles,
        transportOrders,
    ]);

    // Compute primary filter counts from the enriched vehicleData (before primary filtering)
    const countByPrimaryFilter = useMemo(() => {
        let working = 0;
        let notWorking = 0;

        vehicleData.forEach((v) => {
            if (hasActiveWorkTime(v)) {
                working++;
            } else {
                notWorking++;
            }
        });

        // Add employees without vehicles
        const vehicleEmployeeIds = new Set(
            vehicleData.map((v) => v.employeeId?.toLowerCase()).filter(Boolean)
        );
        employees?.forEach((e) => {
            if (vehicleEmployeeIds.has(e.id?.toLowerCase())) return;
            const hasActiveShift = workShifts?.some(
                (w) =>
                    w.employeeId?.toLowerCase() === e.id?.toLowerCase() &&
                    !w.endTime
            );
            if (hasActiveShift) {
                working++;
            } else {
                notWorking++;
            }
        });

        const employeesWithoutVehiclesCount =
            employees?.filter(
                (e) => !vehicleEmployeeIds.has(e.id?.toLowerCase())
            ).length ?? 0;

        return {
            all: vehicleData.length + employeesWithoutVehiclesCount,
            working,
            notWorking,
        };
    }, [vehicleData, employees, workShifts]);

    const filteredVehicleData = useMemo(() => {
        return vehicleData.filter((vehicle) => {
            // Build a mock VehiclePositionInfo for the filter
            const mockPosition = {
                employeeWorkTimeTypes: vehicle.employeeWorkTimeTypes ?? [],
                driving: vehicle.driving,
            } as VehiclePositionInfo;
            return vehiclePrimaryFilter(mockPosition);
        });
    }, [vehicleData, vehiclePrimaryFilter]);

    const filteredEmployeeData: EmployeeData[] = useMemo(
        () =>
            employees
                ?.filter((e) => {
                    const eid = e.id?.toLowerCase();
                    const position = positions?.find((p) =>
                        p.employeeWorkTimeTypes.some(
                            (w) => w.employeeId?.toLowerCase() === eid
                        )
                    );
                    return employeeFilter(position);
                })
                .map((e) => mapEmployeeData(e, positions, workShifts))
                .filter(
                    (p) =>
                        selectedVehicleGroupIds.length === 0 ||
                        selectedVehicleGroupIds.includes(
                            vehicles?.find((v) => v.id === p.vehicleId)
                                ?.vehicleGroupId
                        )
                )
                .sort((a, b) => {
                    if (sortOrder === "employeeName") {
                        return Base.stringCompare(
                            a.employeeName ?? "",
                            b.employeeName ?? ""
                        );
                    }
                    return Base.stringCompare(
                        a.registerNumber,
                        b.registerNumber
                    );
                }) ?? [],
        [
            positions,
            employees,
            primaryFilter,
            noWork,
            selectedWorkTimeTypeIds,
            selectedVehicleGroupIds,
            sortOrder,
            workShifts,
            showWorkingWithoutPhase,
            vehicles,
        ]
    );

    const activeEmployeesAndVehicles = useMemo(() => {
        const activeVehicles = filteredVehicleData.length;
        const activeEmployees = filteredEmployeeData.length;
        const totalEmployees = employees?.length || 0;
        const totalVehicles = positions?.length || 0;
        return {
            activeVehicles,
            totalVehicles,
            activeEmployees,
            totalEmployees,
        };
    }, [filteredVehicleData, filteredEmployeeData, employees, positions]);

    const vehiclesAndEmployeesSidebarProps = useMemo(
        () => ({
            vehicleData: filteredVehicleData,
            employeeData: filteredEmployeeData,
            countByPrimaryFilter,
            transportOrders,
        }),
        [
            filteredVehicleData,
            filteredEmployeeData,
            countByPrimaryFilter,
            transportOrders,
        ]
    );

    const mapDataMemo = useMemo(() => {
        return isVehicleRouteView ? mapData : filteredVehicleData;
    }, [filteredVehicleData, isVehicleRouteView, mapData]);

    return (
        <MainLayout
            noPadding
            sideComponentSide="left"
            sideComponent={
                <MonitorViewSidebar
                    fleetSummaryTotals={activeEmployeesAndVehicles}
                    vehiclesAndEmployeesSidebarProps={
                        vehiclesAndEmployeesSidebarProps
                    }
                />
            }
            sideOverFlowHidden
        >
            <>
                <LoadingIndicator loading={loading} />
                <MonitorViewMap
                    loading={loading}
                    data={mapDataMemo}
                    onRefresh={() => onRefresh(true)}
                    reloadKey={reloadKey}
                />
            </>
        </MainLayout>
    );
};
